//#region IMPORTS
import {
  InstorePresetKey,
  InstoreTypeBucket,
  INSTORE_TYPE_BUCKETS,
  INSTORE_TYPE_BUCKET_LABEL,
  INSTORE_TYPE_BUCKET_COLOR,
} from "./instorePresets";
import { InstoreFlowCountBuckets } from "../../../models/instoreOrder";
import { useAppSelector } from "../../../store/configureStore";
import { Fragment, ReactNode } from "react";
import { Menu } from "@headlessui/react";
import {
  HandThumbUpIcon,
  Cog6ToothIcon,
  TruckIcon,
  CheckBadgeIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from "@heroicons/react/20/solid";
import classNames from "../../../helpers/classNames";
//#endregion

//#region INTERFACE
interface Props {
  activeKey: InstorePresetKey;
  activeType: InstoreTypeBucket | null;
  onSelect: (key: InstorePresetKey, type?: InstoreTypeBucket | null) => void;
  isPicker: boolean;
  isAdmin: boolean;
  isHq: boolean;
}
//#endregion

const PILL_BASE =
  "relative inline-flex items-center gap-1 px-3 py-1 text-sm font-medium rounded-md border transition";

function pillClasses(disabled: boolean, active: boolean) {
  return classNames(
    PILL_BASE,
    disabled
      ? "bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed"
      : active
        ? "bg-gray-300 text-gray-900 border-gray-300"
        : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50",
  );
}

function Bubble({ count, color }: { count: number; color: string }) {
  return (
    <span
      className={classNames(
        "min-w-[1.25rem] h-5 px-1 inline-flex items-center justify-center rounded-full text-xs font-semibold ring-2 ring-white",
        color,
      )}
    >
      {count}
    </span>
  );
}

export default function InstorePresetPills({
  activeKey,
  activeType,
  onSelect,
  isPicker,
  isAdmin,
  isHq,
}: Props) {
  const {
    instoreOrders_pending,
    instoreFlowCounts,
  } = useAppSelector((state) => state.instoreOrders);

  const canHandle = isPicker || isAdmin;

  // A flow pill split per type bucket: 0 → disabled, 1 → direct button,
  // 2 → dropdown. Bubbles are coloured per type.
  const flowPill = (
    key: InstorePresetKey,
    label: string,
    icon: ReactNode,
    buckets: InstoreFlowCountBuckets,
  ) => {
    const active = activeKey === key;
    const present = INSTORE_TYPE_BUCKETS.map((type) => ({
      type,
      count: buckets[type],
    })).filter((b) => b.count > 0);

    const bubbles = (
      <span className="absolute -top-4 -right-2 flex -space-x-0.5">
        {present.map((b) => (
          <Bubble key={b.type} count={b.count} color={INSTORE_TYPE_BUCKET_COLOR[b.type]} />
        ))}
      </span>
    );

    // No work in this status → disabled, no bubble.
    if (present.length === 0) {
      return (
        <button key={key} disabled className={pillClasses(true, active)}>
          {icon}
          {label}
        </button>
      );
    }

    // Exactly one type → direct button into that status + type.
    if (present.length === 1) {
      const only = present[0];
      return (
        <button
          key={key}
          onClick={() => onSelect(key, only.type)}
          className={pillClasses(false, active)}
        >
          {icon}
          {label}
          {bubbles}
        </button>
      );
    }

    const total = present.reduce((sum, b) => sum + b.count, 0);

    // Multiple types → dropdown: pick a single type, or "All" for every type of
    // this status combined in one list.
    return (
      <Menu key={key} as="div" className="relative">
        <Menu.Button className={pillClasses(false, active)}>
          {icon}
          {label}
          <ChevronDownIcon className="w-4 h-4" aria-hidden="true" />
          {bubbles}
        </Menu.Button>
        <Menu.Items className="absolute right-0 z-10 mt-2 w-52 origin-top-right divide-y divide-gray-200 rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none">
          {/* All types of this status combined in one list. */}
          <Menu.Item>
            {({ active: itemActive }) => (
              <button
                onClick={() => onSelect(key)}
                className={classNames(
                  itemActive ? "bg-gray-50" : "",
                  "flex w-full items-center justify-between gap-2 px-4 py-2 text-xs font-medium text-gray-900",
                )}
              >
                <span className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-gray-400" />
                  All types
                </span>
                <Bubble count={total} color="bg-gray-600 text-white" />
              </button>
            )}
          </Menu.Item>
          {present.map((b) => (
            <Menu.Item key={b.type}>
              {({ active: itemActive }) => (
                <button
                  onClick={() => onSelect(key, b.type)}
                  className={classNames(
                    itemActive ? "bg-gray-50" : "",
                    "flex w-full items-center justify-between gap-2 px-4 py-2 text-xs text-gray-700",
                  )}
                >
                  <span className="flex items-center gap-2">
                    <span
                      className={classNames(
                        "h-2 w-2 rounded-full",
                        INSTORE_TYPE_BUCKET_COLOR[b.type],
                      )}
                    />
                    {INSTORE_TYPE_BUCKET_LABEL[b.type]}
                  </span>
                  <Bubble count={b.count} color={INSTORE_TYPE_BUCKET_COLOR[b.type]} />
                </button>
              )}
            </Menu.Item>
          ))}
        </Menu.Items>
      </Menu>
    );
  };

  // HQ approve keeps the original single global-count pill (no type split).
  const hqApprovePill = () => {
    const active = activeKey === "approve";
    const disabled = instoreOrders_pending === 0;
    return (
      <button
        key="approve"
        onClick={() => onSelect("approve")}
        disabled={disabled}
        className={pillClasses(disabled, active)}
      >
        <HandThumbUpIcon className="w-4 h-4" />
        Approve
        {instoreOrders_pending > 0 && (
          <span className="absolute -top-4 -right-2">
            <Bubble
              count={instoreOrders_pending}
              color={active ? "bg-white text-black" : "bg-emerald-500 text-white"}
            />
          </span>
        )}
      </button>
    );
  };

  // The fulfilment workflow pills form one group (Approve → Manage → Send).
  const workflowPills = [
    canHandle &&
      flowPill(
        "approve",
        "Approve",
        <HandThumbUpIcon className="w-4 h-4" />,
        instoreFlowCounts.pending,
      ),
    !canHandle &&
      (isHq || isAdmin || isPicker) &&
      hqApprovePill(),
    canHandle &&
      flowPill(
        "manage",
        "Manage",
        <Cog6ToothIcon className="w-4 h-4" />,
        instoreFlowCounts.ordered,
      ),
    canHandle &&
      flowPill(
        "send",
        "Send",
        <TruckIcon className="w-4 h-4" />,
        instoreFlowCounts.inProgress,
      ),
    canHandle &&
      flowPill(
        "deliver",
        "Deliver",
        <CheckBadgeIcon className="w-4 h-4" />,
        instoreFlowCounts.shipped,
      ),
  ].filter(Boolean);

  return (
    // Flow group sits on the right next to the All button. The gap between the
    // group and All matches the gap-2 between the flow pills so the spacing reads
    // evenly (the flow-pill gap is leading; it also holds the chevrons shown for
    // some roles).
    <div className="flex flex-1 items-center justify-end gap-2">
      {/* Workflow group: Approve → Manage → Send */}
      {workflowPills.length > 0 && (
        <div className="flex items-center gap-2">
          {workflowPills.map((pill, i) => (
            <Fragment key={i}>
              {i > 0 && (
                <ChevronRightIcon
                  className="w-4 h-4 text-gray-400 shrink-0"
                  aria-hidden="true"
                />
              )}
              {pill}
            </Fragment>
          ))}
        </div>
      )}

      {/* Overview preset (type lives in the flow dropdowns now) */}
      <button
        onClick={() => onSelect("all")}
        className={pillClasses(false, activeKey === "all" && !activeType)}
      >
        All
      </button>
    </div>
  );
}
